<template>
  <div class="page-outer">
    <div class="page-header page-header-editor">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯關鍵字規則' : '新增關鍵字規則' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting"><i class="ri-loader-4-line spin"></i> 儲存中...</span>
          <span v-else>{{ isEdit ? '更新' : '建立' }}</span>
        </button>
      </div>
    </div>
    <div class="page-container">

    <div v-if="pageLoading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <template v-else>
      <!-- 手機版預覽開關（只在選範本時顯示） -->
      <template v-if="form.replyType === 'template'">
        <div class="mobile-preview-toggle-bar">
          <button class="mobile-preview-toggle-btn" @click="mobilePreviewOpen = !mobilePreviewOpen">
            <i :class="mobilePreviewOpen ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
            {{ mobilePreviewOpen ? '關閉預覽' : '開啟訊息預覽' }}
          </button>
        </div>

        <div v-if="mobilePreviewOpen" class="mobile-preview-drawer">
          <div class="mobile-preview-header">
            <span><i class="ri-smartphone-line"></i> 訊息預覽
              <span v-if="allPreviewMessages.length > 1" class="preview-count-badge">共 {{ allPreviewMessages.length }} 則</span>
            </span>
            <button class="mobile-preview-close" @click="mobilePreviewOpen = false">
              <i class="ri-close-line"></i>
            </button>
          </div>
          <div class="mobile-preview-body">
            <LineMessagePreview
              :messages="allPreviewMessages"
              empty-text="請從左側選擇訊息範本"
            />
          </div>
        </div>
      </template>

      <!-- 雙欄佈局（選範本時才出現右側預覽） -->
      <div :class="['editor-layout', { 'with-preview': form.replyType === 'template' }]">
        <!-- 左側：表單 -->
        <form class="editor-form" @submit.prevent="handleSubmit">
          <!-- 基本資訊 -->
          <div class="form-card">
            <h3 class="card-title">基本資訊</h3>

            <div class="form-group">
              <label class="required">規則名稱</label>
              <input
                v-model="form.ruleName"
                type="text"
                class="form-control"
                placeholder="請輸入規則名稱"
                maxlength="100"
                required
              />
            </div>

            <div class="form-group">
              <label>啟用</label>
              <label class="switch">
                <input type="checkbox" v-model="form.isActive" />
                <span class="slider"></span>
              </label>
            </div>
          </div>

          <!-- 觸發關鍵字 -->
          <div class="form-card">
            <h3 class="card-title">觸發關鍵字</h3>
            <p class="card-hint">用戶輸入的訊息符合任一關鍵字時觸發</p>

            <div class="keywords-input">
              <div
                v-for="(keyword, index) in form.keywords"
                :key="index"
                class="keyword-input-item"
              >
                <input
                  v-model="form.keywords[index]"
                  type="text"
                  class="form-control"
                  placeholder="輸入關鍵字"
                />
                <button
                  type="button"
                  class="btn-icon-danger"
                  @click="removeKeyword(index)"
                  :disabled="form.keywords.length <= 1"
                >
                  <i class="ri-close-line"></i>
                </button>
              </div>
            </div>
            <button type="button" class="btn-add-keyword" @click="addKeyword">
              <i class="ri-add-line"></i> 新增關鍵字
            </button>
          </div>

          <!-- 回覆設定 -->
          <div class="form-card">
            <h3 class="card-title">回覆設定</h3>

            <!-- 回覆類型選擇 -->
            <div class="form-group">
              <label class="required">回覆類型</label>
              <div class="reply-type-selector">
                <label
                  v-for="opt in replyTypeOptions"
                  :key="opt.value"
                  class="reply-type-option"
                  :class="{ active: form.replyType === opt.value }"
                >
                  <input type="radio" :value="opt.value" v-model="form.replyType" @change="handleReplyTypeChange" />
                  <i :class="opt.icon"></i>
                  <span>{{ opt.label }}</span>
                  <small>{{ opt.hint }}</small>
                </label>
              </div>
            </div>

            <!-- ── 多訊息 slot（支援文字 / 圖片 / 範本）── -->
            <template v-if="form.replyType === 'template'">
              <p class="card-hint" style="margin:0 0 12px">可設定 1–5 則訊息，依序發送給用戶</p>

              <div class="msg-slots">
                <div
                  v-for="(slot, idx) in msgSlots"
                  :key="slotKeys[idx]"
                  class="msg-slot"
                  :class="{ active: activePreviewIdx === idx }"
                >
                  <div class="slot-header" @click="activePreviewIdx = activePreviewIdx === idx ? -1 : idx">
                    <div class="slot-number">{{ idx + 1 }}</div>
                    <div class="slot-title">
                      <template v-if="slot._inputMode === 'text'">
                        <i class="ri-text" style="font-size:13px;color:#b45309;flex-shrink:0;"></i>
                        <span>{{ (slot._text || '').slice(0, 30) || '（未輸入文字）' }}</span>
                      </template>
                      <template v-else-if="slot._inputMode === 'image'">
                        <i class="ri-image-line" style="font-size:13px;color:#15803d;flex-shrink:0;"></i>
                        <span>{{ slot._imageUrl ? '圖片已上傳' : '（未上傳圖片）' }}</span>
                      </template>
                      <template v-else>
                        <span v-if="slot._template">{{ slot._template.name }}</span>
                        <span v-else class="slot-empty-label">未選擇範本</span>
                        <span v-if="slot._template" class="type-badge" :class="`type-${slot._template.messageType}`">{{ msgTypeLabel(slot._template.messageType) }}</span>
                      </template>
                    </div>
                    <div v-if="msgSlots.length > 1" class="slot-order-btns" @click.stop>
                      <button type="button" class="slot-order-btn" :disabled="idx === 0" @click="moveSlot(idx, -1)">
                        <i class="ri-arrow-up-s-line"></i>
                      </button>
                      <button type="button" class="slot-order-btn" :disabled="idx === msgSlots.length - 1" @click="moveSlot(idx, 1)">
                        <i class="ri-arrow-down-s-line"></i>
                      </button>
                    </div>
                    <button v-if="msgSlots.length > 1" type="button" class="slot-remove-btn" @click.stop="removeSlot(idx)">
                      <i class="ri-delete-bin-line"></i>
                    </button>
                  </div>

                  <div v-if="activePreviewIdx === idx" class="slot-picker">
                    <!-- 模式 tab -->
                    <div class="input-mode-tabs">
                      <button type="button" class="mode-tab" :class="{ active: slot._inputMode === 'text' }" @click="slot._inputMode = 'text'">
                        <i class="ri-text"></i> 輸入文字
                      </button>
                      <button type="button" class="mode-tab" :class="{ active: slot._inputMode === 'image' }" @click="slot._inputMode = 'image'">
                        <i class="ri-image-line"></i> 上傳圖片
                      </button>
                      <button type="button" class="mode-tab" :class="{ active: slot._inputMode === 'template' }" @click="slot._inputMode = 'template'">
                        <i class="ri-message-3-line"></i> 選擇範本
                      </button>
                    </div>

                    <!-- 文字輸入 -->
                    <div v-if="slot._inputMode === 'text'" class="slot-mode-body">
                      <textarea
                        v-model="slot._text"
                        class="form-control message-textarea"
                        rows="5"
                        placeholder="請輸入回覆文字內容"
                      ></textarea>
                      <small class="form-text">支援變數：{{memberName}}、{{points}}、{{tierName}} 等</small>
                    </div>

                    <!-- 圖片上傳 -->
                    <div v-else-if="slot._inputMode === 'image'" class="slot-mode-body">
                      <ImageUploader
                        v-model="slot._imageUrl"
                        :uploadFn="uploadImage"
                        shape="card"
                        accept="image/jpeg,image/png"
                        hint="僅支援 JPEG / PNG（LINE 不接受 WebP）"
                        size-hint="最大 4MB，超過請先壓縮"
                        :max-size-mb="4"
                      />
                    </div>

                    <!-- 範本選擇器 -->
                    <LineMsgTemplatePicker
                      v-else
                      :ref="el => pickerRefs[idx] = el"
                      audience-filter="personal"
                      :model-value="slot._template"
                      @update:model-value="val => setSlotTemplate(idx, val)"
                    />
                  </div>
                </div>
              </div>

              <button
                v-if="msgSlots.length < 5"
                type="button"
                class="btn-add-msg"
                @click="addSlot"
              >
                <i class="ri-add-circle-line"></i> 新增訊息（{{ msgSlots.length }}/5）
              </button>
              <div v-else class="hint-box hint-warning" style="margin-top:8px;font-size:13px;">
                <i class="ri-alert-line"></i> 已達最多 5 則訊息上限
              </div>
            </template>

            <!-- FAQ -->
            <div v-if="form.replyType === 'faq'" class="form-group">
              <label class="required">常見問題（FAQ）</label>
              <select v-model="form.replyContent.faqId" class="form-control" required>
                <option value="">請選擇 FAQ</option>
                <option v-for="faq in faqList" :key="faq.id" :value="String(faq.id)">
                  {{ faq.question }}
                </option>
              </select>
            </div>
          </div>

          <!-- 匹配後貼標 -->
          <div class="form-card">
            <h3 class="card-title">匹配後貼標</h3>
            <p class="card-hint">關鍵字匹配且成功回覆後，為該 LINE 好友貼上標籤。互動標籤（manual）重複觸發會累加濃度。</p>
            <div class="form-group">
              <TagSelectorMultiple
                v-model="form.onMatchTagIds"
                label="自動貼標"
                hint="選填，可複選"
              />
            </div>
          </div>

          <!-- 時段控制 -->
          <div class="form-card">
            <h3 class="card-title">時段控制</h3>

            <div class="form-group">
              <label class="switch-label">
                <label class="switch">
                  <input type="checkbox" v-model="form.enableTimeRange" />
                  <span class="slider"></span>
                </label>
                <span>僅在指定時段內啟用此規則</span>
              </label>
            </div>

            <template v-if="form.enableTimeRange">
              <div class="time-range-row">
                <div class="form-group">
                  <label>開始時間</label>
                  <input v-model="form.timeRange.start" type="time" class="form-control" />
                </div>
                <div class="time-range-sep">—</div>
                <div class="form-group">
                  <label>結束時間</label>
                  <input v-model="form.timeRange.end" type="time" class="form-control" />
                </div>
              </div>
              <div class="form-group">
                <label>時區</label>
                <input v-model="form.timeRange.timezone" type="text" class="form-control form-control-narrow" placeholder="Asia/Taipei" />
              </div>
            </template>
          </div>

        </form>

        <!-- 右側：即時預覽（只在選範本時顯示） -->
        <div v-if="form.replyType === 'template'" class="preview-panel">
          <div class="preview-sticky">
            <div class="preview-title">
              <i class="ri-smartphone-line"></i>
              <span>訊息預覽</span>
              <span v-if="allPreviewMessages.length > 1" class="preview-count-badge">
                共 {{ allPreviewMessages.length }} 則
              </span>
            </div>
            <LineMessagePreview
              :messages="allPreviewMessages"
              empty-text="請從左側選擇訊息範本"
            />
          </div>
        </div>
      </div>

    </template>
    </div>
  </div>
</template>

<script>
import { autoReplyService } from '@/api/modules/autoReply.js';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';
import { supportService } from '@/api/modules/support.js';
import { showSuccess, showError } from '@/utils/swal.js';
import LineMessagePreview from '@/components/LineMessagePreview.vue';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import ImageUploader from '@/components/ImageUploader.vue';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import { toLineMsgTemplatePreviewData, MSG_TYPE_LABEL } from '@/utils/lineMsgTemplatePreview.js';

export default {
  name: 'AutoReplyRuleEditor',
  components: { LineMessagePreview, LineMsgTemplatePicker, ImageUploader, TagSelectorMultiple },
  data() {
    return {
      pageLoading: false,
      submitting: false,
      faqList: [],
      mobilePreviewOpen: false,

      // 1-5 個訊息 slot（支援 text / image / template 三種模式）
      msgSlots: [{ _id: 1, _inputMode: 'text', _template: null, _text: '', _textEmojis: [], _imageUrl: '' }],
      slotKeys: [0],
      _keyCounter: 1,
      activePreviewIdx: 0,
      pickerRefs: [],

      form: {
        ruleName: '',
        keywords: [''],
        synonyms: [],
        matchType: 'exact',
        replyType: 'template',
        replyContent: { templateCodes: [], faqId: '', text: '' },
        priority: 0,
        isActive: true,
        enableTimeRange: false,
        timeRange: { start: '09:00', end: '18:00', timezone: 'Asia/Taipei' },
        onMatchTagIds: []
      },

      replyTypeOptions: [
        { value: 'template', label: 'LINE 訊息範本', icon: 'ri-message-3-line', hint: '文字、圖片或訊息範本回覆' },
        { value: 'faq', label: '常見問題', icon: 'ri-question-answer-line', hint: '從 FAQ 庫挑選回覆' }
      ]
    };
  },

  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },

    allPreviewMessages() {
      return this.msgSlots
        .map(slot => {
          if (slot._inputMode === 'text') {
            return (slot._text || '').trim()
              ? { messageType: 'text', textContent: slot._text, textEmojis: slot._textEmojis || [] }
              : null;
          }
          if (slot._inputMode === 'image') {
            return slot._imageUrl ? { messageType: 'image', imageUrl: slot._imageUrl } : null;
          }
          return slot._template ? this._toPreviewData(slot._template) : null;
        })
        .filter(Boolean);
    }
  },

  async mounted() {
    this.loadFaqs();
    if (this.isEdit) {
      await this.loadRule();
    }
  },

  methods: {
    async loadRule() {
      this.pageLoading = true;
      try {
        const res = await autoReplyService.getRule(this.$route.params.id);
        const rule = res.data;
        // 直接取原始 replyContent（保留所有欄位，包含 messages）
        const rawRc = rule.replyContent
          ? (typeof rule.replyContent === 'string' ? JSON.parse(rule.replyContent) : rule.replyContent)
          : {};

        const replyContent = {
          messages: rawRc.messages || null,
          templateCodes: rawRc.templateCodes?.length
            ? rawRc.templateCodes
            : rawRc.templateCode ? [rawRc.templateCode] : [],
          faqId: rawRc.faqId ? String(rawRc.faqId) : '',
          text: rawRc.text || ''
        };

        this.form = {
          ruleName: rule.ruleName || '',
          keywords: rule.keywords?.length ? [...rule.keywords] : [''],
          synonyms: rule.synonyms || [],
          matchType: rule.matchType || 'exact',
          replyType: rule.replyType || 'template',
          replyContent,
          priority: rule.priority || 0,
          isActive: rule.isActive !== undefined ? rule.isActive : true,
          enableTimeRange: rule.enableTimeRange || false,
          timeRange: rule.timeRange || { start: '09:00', end: '18:00', timezone: 'Asia/Taipei' },
          onMatchTagIds: Array.isArray(rule.onMatchTagIds) ? [...rule.onMatchTagIds] : []
        };

        // 若是範本回覆，還原 msgSlots
        if (rule.replyType === 'template') {
          if (rawRc.messages?.length) {
            // 新格式：messages 陣列
            this.msgSlots = rawRc.messages.map((m, i) => {
              if (m.mode === 'text') {
                return {
                  _id: i + 1,
                  _inputMode: 'text',
                  _template: null,
                  _text: m.text || '',
                  _textEmojis: Array.isArray(m.emojis) ? [...m.emojis] : [],
                  _imageUrl: ''
                };
              }
              if (m.mode === 'image') return { _id: i + 1, _inputMode: 'image', _template: null, _text: '', _textEmojis: [], _imageUrl: m.imageUrl || '' };
              return { _id: i + 1, _inputMode: 'template', _template: null, _text: '', _textEmojis: [], _imageUrl: '', _templateId: m.templateId };
            });
            this.slotKeys = this.msgSlots.map(() => this._keyCounter++);
            // 非同步載入 template 物件
            for (let i = 0; i < this.msgSlots.length; i++) {
              const slot = this.msgSlots[i];
              if (slot._inputMode === 'template' && slot._templateId) {
                const res = await lineMsgTemplateService.getById(slot._templateId).catch(() => null);
                if (res?.data) this.msgSlots[i] = { ...this.msgSlots[i], _template: res.data };
              }
            }
          } else if (replyContent.templateCodes.length > 0) {
            // 舊格式：templateCodes
            this.msgSlots = replyContent.templateCodes.map((id, i) => ({
              _id: i + 1,
              _inputMode: 'template',
              _template: null,
              _text: '',
              _textEmojis: [],
              _imageUrl: '',
              _templateId: id
            }));
            this.slotKeys = this.msgSlots.map(() => this._keyCounter++);
            const templates = await Promise.all(
              replyContent.templateCodes.map(id => lineMsgTemplateService.getById(id).then(r => r.data).catch(() => null))
            );
            this.msgSlots = this.msgSlots.map((slot, i) => ({ ...slot, _template: templates[i] }));
          }
        }
      } catch (e) {
        showError('載入規則失敗', e.response?.data?.message || '');
        this.goBack();
      } finally {
        this.pageLoading = false;
      }
    },

    async loadFaqs() {
      try {
        const res = await supportService.listFaqs({ page: 1, pageSize: 100 });
        this.faqList = res.data?.items || [];
      } catch (e) {
        console.error('載入 FAQ 失敗:', e);
      }
    },

    handleReplyTypeChange() {
      this.form.replyContent = { templateCodes: [], faqId: '', text: '' };
      this.msgSlots = [{ _id: 1, _inputMode: 'text', _template: null, _text: '', _textEmojis: [], _imageUrl: '' }];
      this.slotKeys = [0];
      this._keyCounter = 1;
      this.activePreviewIdx = 0;
    },

    // ── 多訊息 slot 管理 ─────────────────────────────────
    addSlot() {
      if (this.msgSlots.length < 5) {
        const maxId = this.msgSlots.reduce((m, s) => Math.max(m, s._id), 0);
        this.msgSlots.push({ _id: maxId + 1, _inputMode: 'text', _template: null, _text: '', _textEmojis: [], _imageUrl: '' });
        this.slotKeys.push(this._keyCounter++);
        this.activePreviewIdx = this.msgSlots.length - 1;
      }
    },

    removeSlot(idx) {
      this.msgSlots.splice(idx, 1);
      this.slotKeys.splice(idx, 1);
      if (this.activePreviewIdx >= this.msgSlots.length) {
        this.activePreviewIdx = this.msgSlots.length - 1;
      }
    },

    moveSlot(idx, dir) {
      const toIdx = idx + dir;
      if (toIdx < 0 || toIdx >= this.msgSlots.length) return;
      const slots = [...this.msgSlots];
      const keys = [...this.slotKeys];
      [slots[idx], slots[toIdx]] = [slots[toIdx], slots[idx]];
      [keys[idx], keys[toIdx]] = [keys[toIdx], keys[idx]];
      this.msgSlots = slots;
      this.slotKeys = keys;
      if (this.activePreviewIdx === idx) this.activePreviewIdx = toIdx;
      else if (this.activePreviewIdx === toIdx) this.activePreviewIdx = idx;
    },

    setSlotTemplate(idx, val) {
      this.msgSlots[idx]._template = val;
    },

    async uploadImage(file) {
      return lineMsgTemplateService.uploadImage(file);
    },

    _toPreviewData(t) {
      return toLineMsgTemplatePreviewData(t);
    },

    msgTypeLabel(type) {
      return MSG_TYPE_LABEL[type] || type;
    },

    addKeyword() {
      this.form.keywords.push('');
    },

    removeKeyword(index) {
      if (this.form.keywords.length > 1) {
        this.form.keywords.splice(index, 1);
      }
    },

    async handleSubmit() {
      if (this.form.replyType === 'template') {
        const hasContent = this.msgSlots.some(s => {
          if (s._inputMode === 'text') return (s._text || '').trim().length > 0;
          if (s._inputMode === 'image') return !!s._imageUrl;
          return !!s._template;
        });
        if (!hasContent) { showError('請至少設定一則訊息內容', ''); return; }
      }
      if (this.form.replyType === 'faq' && !this.form.replyContent.faqId) {
        showError('請選擇 FAQ', '');
        return;
      }

      this.submitting = true;
      try {
        let replyContent;
        if (this.form.replyType === 'template') {
          const messages = this.msgSlots
            .map(s => {
              if (s._inputMode === 'text') {
                if (!(s._text || '').trim()) return null;
                const row = { mode: 'text', text: s._text };
                if (Array.isArray(s._textEmojis) && s._textEmojis.length) row.emojis = s._textEmojis;
                return row;
              }
              if (s._inputMode === 'image') return s._imageUrl ? { mode: 'image', imageUrl: s._imageUrl } : null;
              return s._template ? { mode: 'template', templateId: s._template.id } : null;
            })
            .filter(Boolean);
          replyContent = { messages };
        } else {
          replyContent = { faqId: this.form.replyContent.faqId };
        }

        const data = {
          ...this.form,
          keywords: this.form.keywords.filter(k => k.trim() !== ''),
          replyContent
        };

        if (this.isEdit) {
          await autoReplyService.updateRule(this.$route.params.id, data);
          await showSuccess('更新成功', '');
        } else {
          await autoReplyService.createRule(data);
          await showSuccess('建立成功', '');
        }
        this.goBack();
      } catch (e) {
        showError('提交失敗', e.response?.data?.message || '');
      } finally {
        this.submitting = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'AutoReplyRules' });
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 18px;
  color: #6b7280;
  transition: all 0.2s;
}
.btn-back:hover { border-color: #3b82f6; color: #3b82f6; }


/* 佈局：單欄，右側預覽浮動 */
.editor-layout {
  display: block;
}
.editor-layout.with-preview .editor-form {
  margin-right: 344px;
}

/* 浮動預覽面板 */
.preview-panel {
  /* DOM 容器，實際預覽由 fixed 子元素呈現 */
}
.preview-sticky {
  position: fixed;
  top: 110px;
  right: 28px;
  width: 320px;
  max-height: calc(100vh - 130px);
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 90;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  padding: 12px 0 0;
}
.preview-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  padding: 0 16px 12px;
  border-bottom: 1px solid #e5e7eb;
}
.preview-title i { font-size: 16px; color: #06C755; }

/* 左側表單 */
.editor-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 24px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
}
.card-hint {
  font-size: 12px;
  color: #9ca3af;
  margin: -8px 0 12px 0;
}

.inline-group {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.form-group {
  margin-bottom: 16px;
}
.form-group:last-child { margin-bottom: 0; }
.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}
.form-group label.required::after { content: ' *'; color: #ef4444; }

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}
.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
.form-control-narrow { max-width: 160px; }

.form-text {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
  display: block;
}

/* 關鍵字 */
.keywords-input {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}
.keyword-input-item {
  display: flex;
  gap: 8px;
  align-items: center;
}
.keyword-input-item .form-control { flex: 1; }

.btn-add-keyword {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
}
.btn-add-keyword:hover { border-color: #3b82f6; color: #3b82f6; }

.btn-icon-danger {
  background: none;
  border: 1px solid #fca5a5;
  color: #ef4444;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  font-size: 14px;
  flex-shrink: 0;
}
.btn-icon-danger:hover:not(:disabled) { background: #fef2f2; }
.btn-icon-danger:disabled { opacity: 0.4; cursor: not-allowed; }

/* 回覆類型 */
.reply-type-selector {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.reply-type-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  min-width: 130px;
  transition: all 0.2s;
}
.reply-type-option input { display: none; }
.reply-type-option i { font-size: 20px; color: #9ca3af; }
.reply-type-option span { font-size: 13px; font-weight: 600; color: #374151; }
.reply-type-option small { font-size: 11px; color: #9ca3af; }
.reply-type-option.active { border-color: #3b82f6; background: #eff6ff; }
.reply-type-option.active i, .reply-type-option.active span { color: #3b82f6; }

/* 多訊息 slots */
.reply-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.btn-add-msg {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 13px;
  border: 2px dashed #93c5fd;
  border-radius: 10px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  transition: all .2s;
  box-sizing: border-box;
}
.btn-add-msg i { font-size: 18px; }
.btn-add-msg:hover { border-color: #3b82f6; color: #1d4ed8; background: #dbeafe; }
.btn-add-msg:disabled { opacity: .45; cursor: not-allowed; }

.hint-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
}
.hint-warning { background: #fffbeb; color: #92400e; border: 1px solid #fcd34d; }

.msg-slots { display: flex; flex-direction: column; gap: 8px; }
.msg-slot { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; transition: border-color 0.15s; }
.msg-slot.active { border-color: #3b82f6; }

.slot-header { display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: #f9fafb; cursor: pointer; user-select: none; }
.msg-slot.active .slot-header { background: #eff6ff; }

.slot-number {
  width: 22px; height: 22px; border-radius: 50%; background: #e5e7eb;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: #6b7280; flex-shrink: 0;
}
.msg-slot.active .slot-number { background: #3b82f6; color: #fff; }

.slot-title { flex: 1; display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #111827; min-width: 0; overflow: hidden; }
.slot-title > span:first-child { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.slot-empty-label { color: #9ca3af; font-weight: 400; font-style: italic; }

.type-badge { font-size: 10px; padding: 1px 6px; border-radius: 10px; font-weight: 600; background: #f3f4f6; color: #6b7280; white-space: nowrap; flex-shrink: 0; }
.type-badge.type-text     { background: #fef3c7; color: #b45309; }
.type-badge.type-image    { background: #dcfce7; color: #15803d; }
.type-badge.type-video    { background: #fce7f3; color: #be185d; }
.type-badge.type-flex, .type-badge.type-flex_carousel { background: #ede9fe; color: #7c3aed; }
.type-badge.type-image_carousel { background: #e0f2fe; color: #0369a1; }
.type-badge.type-imagemap { background: #ffedd5; color: #c2410c; }

.slot-order-btns { display: flex; flex-direction: row; gap: 2px; flex-shrink: 0; }
.slot-order-btn { background: none; border: 1px solid #e5e7eb; border-radius: 4px; cursor: pointer; color: #6b7280; font-size: 14px; padding: 1px 4px; line-height: 1; display: flex; align-items: center; }
.slot-order-btn:hover:not(:disabled) { border-color: #3b82f6; color: #3b82f6; }
.slot-order-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.slot-remove-btn { background: none; border: none; cursor: pointer; font-size: 15px; padding: 3px; border-radius: 4px; flex-shrink: 0; color: #9ca3af; }
.slot-remove-btn:hover { color: #ef4444; }

.slot-picker { background: #fff; border-top: 1px solid #e5e7eb; }

/* 模式 tab */
.input-mode-tabs {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  padding: 0 12px;
  margin-bottom: 14px;
}
.mode-tab {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: -2px;
  transition: all .15s;
}
.mode-tab:hover { color: #3b82f6; }
.mode-tab.active { color: #3b82f6; border-bottom-color: #3b82f6; font-weight: 600; }

.slot-mode-body { padding: 12px; }

.message-textarea {
  width: 100%;
  resize: vertical;
  min-height: 120px;
  font-size: 14px;
  line-height: 1.6;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
}
.message-textarea:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.1); }

.preview-count-badge { margin-left: auto; font-size: 11px; background: #dbeafe; color: #1d4ed8; padding: 2px 8px; border-radius: 10px; font-weight: 600; }

/* switch */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}
.switch input { opacity: 0; width: 0; height: 0; }
.slider {
  position: absolute;
  inset: 0;
  background: #d1d5db;
  border-radius: 24px;
  transition: 0.2s;
  cursor: pointer;
}
.slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
input:checked + .slider { background: #3b82f6; }
input:checked + .slider::before { transform: translateX(20px); }

.switch-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
}

/* 時段 */
.time-range-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}
.time-range-sep { padding-bottom: 10px; color: #6b7280; }

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-container {
  padding-top: 72px;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  margin-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.header-actions .btn {
  padding: 6px 12px;
  font-size: 13px;
}

.btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover { background: #e5e7eb; }

.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.loading-state {
  text-align: center;
  padding: 60px;
  color: #9ca3af;
}

/* 手機版預覽開關（桌機隱藏） */
.mobile-preview-toggle-bar {
  display: none;
}
.mobile-preview-drawer {
  display: none;
}

@media (max-width: 900px) {
  .editor-layout.with-preview .editor-form {
    margin-right: 0;
  }
  .preview-panel,
  .preview-sticky {
    display: none;
  }
  .form-actions-bar {
    left: 0;
    right: 0 !important;
  }

  .mobile-preview-toggle-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }

  .mobile-preview-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: 1px solid #06C755;
    border-radius: 20px;
    background: #fff;
    color: #06C755;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all .15s;
  }
  .mobile-preview-toggle-btn:hover {
    background: #f0fff5;
  }

  .mobile-preview-drawer {
    display: block;
    margin-bottom: 12px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    background: #fff;
  }

  .mobile-preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }
  .mobile-preview-header i {
    color: #06C755;
    margin-right: 4px;
  }

  .mobile-preview-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 6px;
    background: #f3f4f6;
    color: #6b7280;
    cursor: pointer;
    font-size: 16px;
  }
  .mobile-preview-close:hover {
    background: #e5e7eb;
  }

  .mobile-preview-body {
    padding: 16px;
  }
}
</style>

<style>
.page-header-editor {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}

.sidebar.collapsed ~ .main-content .page-header-editor {
  left: 64px;
}

@media (max-width: 768px) {
  .page-header-editor {
    left: 0 !important;
    top: 56px;
  }
}
</style>
